import React, { useCallback, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { xcss, Inline, Box, Flex } from '@atlaskit/primitives';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { SearchField as SearchFieldComponent } from '@atlassian/jira-searchfield/src/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useTextSearchInputClause } from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/use-text-search-input-clause.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useASTActions, useAST } from '../../controllers/ast/index.tsx';
import { TEXT_FIELDS } from '../../common/constants.tsx';
import KeyboardShortcuts from './keyboard-shortcuts/index.tsx';
import { update, getValue, getUnsupportedCharacters } from './utils.tsx';
import messages from './messages.tsx';
import { getTextSearchInputClause } from './utils/getTextSearchInputClause.tsx';

type Props = {
	/**
	 * unique string identifying the input label for screen reader users and used for placeholder text
	 */
	inputLabel: string;
	/**
	 * `true` when the input should be disabled e.g. when search result is still loading
	 */
	isDisabled?: boolean;
};

type DebounceOnChangeProps = {
	targetValue: string;
	onChange: (arg1: string) => void;
};
const debounceOnChange = debounce(
	({ onChange, targetValue }: DebounceOnChangeProps) => {
		onChange(targetValue);
	},
	500,
	{
		leading: false,
		trailing: true,
	},
);

const searchFieldContainerStyles = xcss({
	width: '100%',
	maxWidth: '160px',
});

const SearchField = ({ inputLabel, isDisabled: isDisabledProp = false }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { updateJql } = useASTActions();
	const searchRef = useRef<HTMLInputElement>(null);
	const valueWithSpecialCharacters = useRef<string | undefined>();

	const ast = useAST();

	const textSearchInputClauseOld = useTextSearchInputClause(ast, TEXT_FIELDS);
	const { textSearchInputClause: textSearchInputClauseNew, isValid } = useMemo(() => {
		if (fg('list_lovability_improving_filters')) {
			return getTextSearchInputClause(ast, TEXT_FIELDS);
		}
		return { textSearchInputClause: undefined, isValid: true };
	}, [ast]);

	const textSearchInputClause = fg('list_lovability_improving_filters')
		? textSearchInputClauseNew
		: textSearchInputClauseOld;

	const isDisabled = useMemo(() => {
		if (fg('list_lovability_improving_filters')) {
			return isDisabledProp || !isValid;
		}
		return isDisabledProp;
	}, [isDisabledProp, isValid]);

	const value = useMemo(() => {
		if (fg('list_lovability_improving_filters')) {
			return (isValid && getValue(textSearchInputClause, valueWithSpecialCharacters.current)) || '';
		}

		return getValue(textSearchInputClause, valueWithSpecialCharacters.current) || '';
	}, [textSearchInputClause, isValid]);

	const handleSearch = useCallback(
		(newSearchValue: string) => {
			const unsupportedCharacters = getUnsupportedCharacters(newSearchValue);

			valueWithSpecialCharacters.current = unsupportedCharacters?.length
				? newSearchValue
				: undefined;

			const updatedSearchString = update(ast, textSearchInputClause, newSearchValue);
			updateJql({ jql: updatedSearchString, fieldType: TEXT_FIELDS });

			if (unsupportedCharacters) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'filterRefinement searchWithUnsupportedCharacters',
					{
						unsupportedCharacters: [...new Set(unsupportedCharacters)],
					},
				);
			}
		},
		[ast, createAnalyticsEvent, textSearchInputClause, updateJql],
	);

	const handleChange = useCallback(
		(targetValue: string) => {
			debounceOnChange({
				onChange: handleSearch,
				targetValue,
			});
		},
		[handleSearch],
	);

	const handleFocus = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'focused',
			actionSubject: 'input',
			actionSubjectId: 'search',
			attributes: { keyboardShortcut: 'false' },
		});
	}, [createAnalyticsEvent]);

	const formattedMessageValues = useMemo(() => {
		return {
			inline: (parts: React.ReactNode[]) => (
				<Box padding="space.050" xcss={keyboardShortcutTagStyles}>
					{parts}
				</Box>
			),
		};
	}, []);

	const formattedTooltipContent = useMemo(() => {
		return (
			<Flex
				xcss={tooltipContentStyles}
				alignItems="center"
				justifyContent="center"
				columnGap="space.050"
			>
				<FormattedMessage {...messages.tooltipMessage} values={formattedMessageValues} />
			</Flex>
		);
	}, [formattedMessageValues]);

	return (
		<>
			<KeyboardShortcuts searchRef={searchRef} />
			<Inline xcss={searchFieldContainerStyles}>
				<SearchFieldComponent
					onChange={handleChange}
					onFocus={handleFocus}
					value={value}
					placeholder={inputLabel}
					placeholderAlwaysVisible
					isCompact
					isResponsive
					inputRef={searchRef}
					isDisabled={isDisabled}
					showIconBeforeInput
					shouldFitContainerWidth
					testId="filter-refinement.ui.search-field"
					renderTooltipContent={formattedTooltipContent}
				/>
			</Inline>
		</>
	);
};

export default SearchField;

const keyboardShortcutTagStyles = xcss({
	display: 'inline-flex',
	height: '16px',
	alignItems: 'center',
	color: 'color.text.inverse',
	borderColor: 'color.border.inverse',
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderStyle: 'solid',
});

const tooltipContentStyles = xcss({
	textAlign: 'center',
	paddingInline: 'space.075',
	paddingBlock: 'space.025',
});
